import { Drawer, Flex, ScrollArea, Stack, UnstyledButton } from "@mantine/core";
import { useViewportSize } from "@mantine/hooks";
import type { User_role } from "@prisma/client";
import cx from "classnames";
import { signOut } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { memo, useEffect, useState } from "react";
import { MdOutlineClose } from "react-icons/md";
import {
  ADMIN_AUTHORITY_PATH,
  COACH_AUTHORITY_PATH,
  DRAWER_MENU_PATH,
  FREE_USER_DRAWER_MENU_PATH,
} from "src/const/paths";
import { font } from "src/lib/nextfont/LocalFontProvider";
import { useGetUserSettingsQuery } from "src/service/UsersApi";

import styles from "./DrawerMenu.module.scss";

export const DrawerMenu = ({
  drawerOpen,
  setDrawerOpen,
  toggleDrawer,
}: {
  drawerOpen: boolean;
  setDrawerOpen: Dispatch<SetStateAction<boolean>>;
  toggleDrawer: () => void;
}) => {
  const router = useRouter();
  const { height } = useViewportSize();
  const { data: user } = useGetUserSettingsQuery();
  const [navItemProps, serNavItemsProps] = useState<
    {
      href: string;
      icon: ReactNode;
      label: string;
    }[]
  >([]);

  // ページに遷移が開始したらドロワーが閉じように変更
  useEffect(() => {
    const closeDrawer = () => {
      setDrawerOpen(false);
    };
    router.events.on("routeChangeStart", closeDrawer);
    return () => {
      router.events.off("routeChangeStart", closeDrawer);
    };
  }, [router.events, setDrawerOpen]);

  // 権限でメニューの内容を変更
  useEffect(() => {
    if (user) {
      const role = user.role;
      const isAdmin =
        role === "ADMIN" ||
        (user.isUpgradedRole &&
          user.userRoles.some(({ role }) => role === "ADMIN"));
      const logoutItem = DRAWER_MENU_PATH.filter((item) => {
        return item.label === "ログアウト";
      })[0];
      const OmitLogoutItems = DRAWER_MENU_PATH.filter((item) => {
        return item.label !== "ログアウト";
      });

      if (isAdmin) {
        return serNavItemsProps([
          ...OmitLogoutItems,
          COACH_AUTHORITY_PATH,
          ADMIN_AUTHORITY_PATH,
          logoutItem,
        ]);
      } else if (role === "COACH") {
        return serNavItemsProps([
          ...OmitLogoutItems,
          COACH_AUTHORITY_PATH,
          logoutItem,
        ]);
      } else if (role === "STUDENT") {
        return serNavItemsProps([...DRAWER_MENU_PATH]);
      } else {
        return serNavItemsProps([...FREE_USER_DRAWER_MENU_PATH]);
      }
    } else {
      return serNavItemsProps([...FREE_USER_DRAWER_MENU_PATH]);
    }
  }, [user]);

  const NavItems = navItemProps.map((navItem) => {
    return <DrawerNav key={navItem.label} {...navItem} />;
  });

  return (
    <Drawer
      opened={drawerOpen}
      onClose={toggleDrawer}
      position="right"
      withCloseButton={false}
      overlayOpacity={0.5}
      size={262}
      zIndex={150}
    >
      <ScrollArea style={{ height: height - 64 }}>
        <div className={styles.drawerInner}>
          <div className={styles.closeButtonWrap}>
            <button
              className={styles.closeButton}
              onClick={toggleDrawer}
              aria-label="ドロワーメニューを閉じる"
            >
              <MdOutlineClose size={24} />
            </button>
          </div>
          <div className={styles.logoWrap}>
            <Image
              src="/images/logo.svg"
              alt="TANQ BASEのロゴ"
              width={130}
              height={27}
              className={styles.logo}
            />
          </div>
          <Stack mt={30} spacing={0}>
            {NavItems}
          </Stack>
        </div>
      </ScrollArea>
    </Drawer>
  );
};

export const DrawerMenuMemo = memo(DrawerMenu);

const DrawerNav = ({
  href,
  icon,
  label,
  roles,
  openNewTab,
}: {
  href: string;
  icon: ReactNode;
  label: string;
  roles?: User_role[];
  openNewTab?: boolean;
}) => {
  const { data: user } = useGetUserSettingsQuery();

  if (roles?.length && user && !roles.includes(user?.role)) {
    return null;
  }

  return (
    <div>
      {label === "ログアウト" ? (
        <UnstyledButton
          className={cx(styles.link, font.className)}
          onClick={() => {
            signOut();
          }}
        >
          <Flex align="center" gap={10}>
            {icon}
            {label}
          </Flex>
        </UnstyledButton>
      ) : label === "ガイドライン" ? (
        <Link
          href={href}
          className={cx(styles.link, font.className)}
          target="_blank"
          passHref
          rel="noopener noreferrer"
        >
          <Flex align="center" gap={10}>
            {icon}
            {label}
          </Flex>
        </Link>
      ) : (
        <Link
          href={href}
          className={cx(styles.link, font.className)}
          target={openNewTab ? "_blank" : undefined}
        >
          <Flex align="center" gap={10}>
            {icon}
            {label}
          </Flex>
        </Link>
      )}
    </div>
  );
};
